import React from "react";
import { Award, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { teamI18n } from "../i18n/team_i18n";
import alinaPhoto from "../assets/team/alina-grigoryan.jpg";
import olhaPhoto from "../assets/team/olha-kudrya.jpg";
import anastasiaPhoto from "../assets/team/anastasia-kyryk.jpg";
import vladaPhoto from "../assets/team/vlada-vasylchuk.jpg";
import zhannaPhoto from "../assets/team/zhanna-atarshchikova.jpg";
import katerynaPhoto from "../assets/team/kateryna-koshyl.jpg";
import olenaPhoto from "../assets/team/olena-pechurina.jpg";
import yuliaPhoto from "../assets/team/yulia-khalilova.jpg";
import mariiaPhoto from "../assets/team/mariia-fashion.jpg";
import teamGroupPhoto from "../assets/team/team-group.jpg";

const memberKeys = ["AK", "VV", "YK", "ZA", "OP", "KK1", "OK", "MR", "NT", "VD", "VI", "TT"];
const monograms = { KK1: "KK", YK: "YK", ZA: "ZA", VV: "VV", AK: "AK", OK: "OK", OP: "OP", NT: "NT", VD: "VD", VI: "VI", TT: "TT", MR: "MR" };
const photos = { OK: olhaPhoto, AK: anastasiaPhoto, VV: vladaPhoto, ZA: zhannaPhoto, KK1: katerynaPhoto, OP: olenaPhoto, YK: yuliaPhoto, MR: mariiaPhoto };

const Monogram = ({ letters, size = 96 }) => (
  <div className="rounded-full flex items-center justify-center mx-auto relative" style={{ width: size, height: size, background: '#1B2A4A', boxShadow: '0 12px 30px -10px rgba(27,42,74,0.4)' }}>
    <span style={{ color: '#C9A96E', fontFamily: "'Forum', Georgia, serif", fontWeight: 400, fontSize: size * 0.42, letterSpacing: '0.04em' }}>{letters}</span>
    <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: '1px solid rgba(201,169,110,0.3)' }} />
  </div>
);

const MemberPhoto = ({ src, alt, size = 96 }) => (
  <div className="rounded-full overflow-hidden mx-auto relative" style={{ width: size, height: size, boxShadow: '0 12px 30px -10px rgba(27,42,74,0.35)', border: '2px solid #C9A96E' }}>
    <img src={src} alt={alt} className="w-full h-full object-cover" style={{ objectPosition: 'center 22%' }} loading="lazy" />
  </div>
);

const Team = ({ onOpenForm, showMembers = true }) => {
  const { lang } = useLang();
  const data = teamI18n[lang] || teamI18n.en;
  const founder = data.founder;
  const badges = ["ICEF", "BBSN", "English UK", "Navitas Champions", "BC Agent Training"];

  return (
    <section id="team" className="py-20 md:py-28" style={{ background: '#FAF8F5' }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {showMembers && (
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="eyebrow mb-3">Team</div>
            <h2 className="headline-huge text-3xl md:text-5xl text-balance" style={{ color: '#1B2A4A' }}>{data.title}</h2>
            <p className="mt-4 text-base md:text-lg" style={{ color: '#666666' }}>{data.subtitle}</p>
          </div>
        )}

        {showMembers && (
          <div className="relative rounded-3xl p-6 md:p-10 mb-16 grid lg:grid-cols-12 gap-8 md:gap-10 items-center" style={{ background: '#FFFFFF', border: '1px solid #E8E0D5', boxShadow: '0 20px 60px -30px rgba(27,42,74,0.18)' }}>
            <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest z-10" style={{ background: '#1B2A4A', color: '#C9A96E' }}>
              <Sparkles className="w-3 h-3" /> {data.founderBadge}
            </div>
            <div className="lg:col-span-7">
              <div className="flex flex-col sm:flex-row items-center gap-6 md:gap-8 mb-6">
                <div className="rounded-full overflow-hidden flex-shrink-0" style={{ width: 200, height: 200, boxShadow: '0 22px 54px -14px rgba(27,42,74,0.5)', border: '3px solid #C9A96E' }}>
                  <img src={alinaPhoto} alt="Alina Grigoryan — CEO STUDYGRI" className="w-full h-full object-cover" data-testid="founder-photo" />
                </div>
                <div className="text-center sm:text-left">
                  <div className="font-display text-3xl md:text-4xl leading-tight" style={{ color: '#1B2A4A' }}>{founder.name}</div>
                  <div className="font-serif-italic text-base md:text-lg mt-1.5" style={{ color: '#C9A96E', fontStyle: 'italic' }}>{founder.title}</div>
                </div>
              </div>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: '#2C2C2C' }}>{founder.bio}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {badges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: '#FAF8F5', color: '#1B2A4A', border: '1px solid #C9A96E' }}>
                    <Award className="w-3 h-3" style={{ color: '#C9A96E' }} /> {b}
                  </span>
                ))}
              </div>
              <button onClick={onOpenForm} className="mt-7 inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-white transition hover:opacity-90" style={{ background: '#1B2A4A' }}>
                {data.consultBtn}
              </button>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: '0 24px 60px -20px rgba(27,42,74,0.35)', border: '1px solid rgba(201,169,110,0.35)' }}>
                <img src={teamGroupPhoto} alt="STUDYGRI team" className="w-full h-auto block" loading="lazy" data-testid="team-group-photo" />
              </div>
            </div>
          </div>
        )}

        {!showMembers && (
        <div className="relative rounded-3xl p-8 md:p-12 mb-16 grid lg:grid-cols-12 gap-10 items-center" style={{ background: '#FFFFFF', border: '1px solid #E8E0D5', boxShadow: '0 20px 60px -30px rgba(27,42,74,0.18)' }}>
          <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: '#1B2A4A', color: '#C9A96E' }}>
            <Sparkles className="w-3 h-3" /> {data.founderBadge}
          </div>
          <div className="lg:col-span-4 flex flex-col items-center text-center">
            <div className="relative rounded-full overflow-hidden mx-auto" style={{ width: 200, height: 200, boxShadow: '0 18px 50px -12px rgba(27,42,74,0.45)', border: '3px solid #C9A96E' }}>
              <img src={alinaPhoto} alt="Alina Grigoryan — CEO STUDYGRI" className="w-full h-full object-cover" />
            </div>
            <div className="font-display text-2xl md:text-3xl mt-5" style={{ color: '#1B2A4A' }}>{founder.name}</div>
            <div className="font-serif-italic text-base mt-1" style={{ color: '#C9A96E', fontStyle: 'italic' }}>{founder.title}</div>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: '#2C2C2C' }}>{founder.bio}</p>
            <div className="mt-7 flex flex-wrap gap-2">
              {badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: '#FAF8F5', color: '#1B2A4A', border: '1px solid #C9A96E' }}>
                  <Award className="w-3 h-3" style={{ color: '#C9A96E' }} /> {b}
                </span>
              ))}
            </div>
            <button onClick={onOpenForm} className="mt-8 inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-white transition hover:opacity-90" style={{ background: '#1B2A4A' }}>
              {data.consultBtn}
            </button>
          </div>
        </div>
        )}

        {showMembers && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {memberKeys.map((k) => {
            const m = data.members[k];
            return (
              <div key={k} className="group rounded-2xl p-6 text-center transition-all duration-500" style={{ background: '#FFFFFF', border: '1px solid rgba(201,169,110,0.4)', boxShadow: '0 6px 20px -10px rgba(27,42,74,0.08)' }}>
                <div className="transition-transform duration-500 group-hover:scale-105">
                  {photos[k] ? (
                    <MemberPhoto src={photos[k]} alt={m.n} size={88} />
                  ) : (
                    <Monogram letters={monograms[k]} size={88} />
                  )}
                </div>
                <h3 className="font-display text-lg md:text-xl mt-5 leading-tight" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.02em' }}>{m.n}</h3>
                <div className="font-serif-italic text-sm mt-1.5" style={{ color: '#C9A96E', fontStyle: 'italic' }}>{m.r}</div>
                <div className="mx-auto my-4 w-10 h-px" style={{ background: '#C9A96E' }} />
                <p className="text-sm leading-relaxed" style={{ color: '#666666' }}>{m.b}</p>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
};

export default Team;
